import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Person,
  Email,
  Security,
  Save,
  Edit,
  Cancel,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LocaleSettingsCard } from '../components/LocaleSettingsCard';
import { PrivacyPreferencesCard } from '../components/PrivacyPreferencesCard';
import { NotificationPreferencesCard } from '../components/NotificationPreferencesCard';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { PageContainer, PageHeader } from '../components/ui';
import { useToast } from '../contexts/ToastContext';

export const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const { hash } = useLocation();
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [error, setError] = useState('');
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editPasswordOpen, setEditPasswordOpen] = useState(false);

  // The profile menu links straight at a card (e.g. #notification-preferences);
  // the router does not scroll to a hash on its own.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  const handleUpdateProfile = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError('');

    try {
      await updateProfile(currentUser, {
        displayName: displayName.trim(),
      });
      showToast('Profile updated successfully!', 'success');
      setEditProfileOpen(false);
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentUser || !currentPassword || !newPassword) return;

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      
      showToast('Password updated successfully!', 'success');
      setEditPasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setError(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageContainer>
        <PageHeader title="Profile" description="Manage your account information and security settings." />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Profile Information Card */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Person sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Profile Information
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setEditProfileOpen(true)}
                  sx={{ 
                    bgcolor: 'grey.100',
                    '&:hover': { bgcolor: 'grey.300' }
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
              
              <Box display="flex" alignItems="center" mb={3} p={2} bgcolor="grey.50" borderRadius={2}>
                <Avatar
                  sx={{ 
                    width: 64, 
                    height: 64, 
                    mr: 2,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {currentUser?.displayName || 'No name set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentUser?.email}
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Display Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentUser?.displayName || 'No name set'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentUser?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Email cannot be changed. Contact support if needed.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Security Settings Card */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Security sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Security Settings
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setEditPasswordOpen(true)}
                  sx={{ 
                    bgcolor: 'grey.100',
                    '&:hover': { bgcolor: 'grey.300' }
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Password Status
                  </Typography>
                  <Chip label="Protected" color="success" size="small" />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Your account password is secure. Click the edit button to change your password.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Account Information Card */}
          <Card sx={{ 
            gridColumn: { xs: '1', md: '1 / -1' },
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <Email sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Account Information
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    User ID
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {currentUser?.uid}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Created
                  </Typography>
                  <Typography variant="body1">
                    {currentUser?.metadata.creationTime
                      ? new Date(currentUser.metadata.creationTime).toLocaleDateString()
                      : 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Sign In
                  </Typography>
                  <Typography variant="body1">
                    {currentUser?.metadata.lastSignInTime
                      ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString()
                      : 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email Verified
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label={currentUser?.emailVerified ? 'Yes' : 'No'} 
                      color={currentUser?.emailVerified ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Locale Settings Card */}
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <LocaleSettingsCard />
          </Box>

          {/* Notification Preferences Card */}
          <Box id="notification-preferences" sx={{ gridColumn: { xs: '1', md: '1 / -1' }, scrollMarginTop: '24px' }}>
            <NotificationPreferencesCard />
          </Box>

          {/* Privacy Preferences Card */}
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <PrivacyPreferencesCard />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}


        {/* Edit Profile Dialog */}
        <Dialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Profile</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              sx={{ mt: 1 }}
            />
            <TextField
              fullWidth
              label="Email"
              value={email}
              disabled
              margin="normal"
              helperText="Email cannot be changed. Contact support if needed."
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setEditProfileOpen(false)}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleUpdateProfile}
              disabled={loading || !displayName.trim()}
            >
              {loading ? <CircularProgress size={20} /> : 'Update Profile'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Password Dialog */}
        <Dialog
          open={editPasswordOpen}
          onClose={() => setEditPasswordOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold' }}>Change Password</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              sx={{ mt: 1 }}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Password must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setEditPasswordOpen(false)}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleUpdatePassword}
              disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            >
              {loading ? <CircularProgress size={20} /> : 'Update Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </PageContainer>
    </>
  );
};
